import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Game } from "@/types";
import { formatDate, getTeamById, stageLabel } from "@/utils/nba";

interface HeroCarouselProps {
  games: Game[];
}

const slideImages = [
  "https://readdy.ai/api/search-image?query=Cinematic%20wide%20view%20of%20a%20packed%20modern%20basketball%20arena%20at%20night%20with%20dramatic%20spotlights%20and%20glowing%20hardwood%20court%2C%20deep%20navy%20blue%20atmosphere%20with%20warm%20orange%20light%20accents%2C%20hazy%20energetic%20sports%20broadcast%20mood%2C%20dark%20moody%20background%20with%20strong%20contrast%20for%20text%20overlay%2C%20ultra%20detailed%20professional%20photography&width=1920&height=900&seq=nba-hero-arena&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Dynamic%20close%20up%20of%20a%20basketball%20spinning%20above%20the%20court%20with%20motion%20blur%20and%20rim%20lighting%2C%20dark%20navy%20blue%20arena%20background%20with%20orange%20glow%20accents%2C%20dramatic%20side%20lighting%2C%20cinematic%20sports%20hero%20image%20with%20open%20space%2C%20ultra%20detailed%20professional%20photography&width=1920&height=900&seq=nba-hero-ball&orientation=landscape",
  "https://readdy.ai/api/search-image?query=Stylized%20silhouette%20of%20basketball%20players%20jumping%20for%20a%20rebound%20against%20a%20dark%20navy%20gradient%20background%20with%20orange%20light%20streaks%20and%20lens%20flare%2C%20energetic%20abstract%20sports%20texture%2C%20cinematic%20lighting%20with%20negative%20space%20for%20headline%20text%2C%20ultra%20detailed&width=1920&height=900&seq=nba-hero-dunk&orientation=landscape",
];

export default function HeroCarousel({ games }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (games.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % games.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [games.length]);

  if (games.length === 0) return null;

  const go = (next: number) => {
    setIndex((next + games.length) % games.length);
  };

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      {games.map((game, i) => {
        const home = getTeamById(game.homeTeamId);
        const away = getTeamById(game.awayTeamId);
        if (!home || !away) return null;
        const image = slideImages[i % slideImages.length];
        return (
          <div
            key={game.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          >
            <img
              src={image}
              alt={`${home.fullName} 对阵 ${away.fullName}`}
              title={`${home.fullName} vs ${away.fullName} NBA比赛直播`}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/80 via-foreground-950/50 to-foreground-950/20" />
            <div className="absolute inset-0">
              <div className="mx-auto flex h-full max-w-page flex-col justify-center px-4 md:px-6">
                <div className="w-full max-w-2xl">
                  <div className="mb-4 flex items-center gap-2">
                    <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                      {stageLabel(game.stage)}
                    </span>
                    {game.status === "live" ? (
                      <span className="flex items-center gap-1 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                        <span className="w-1.5 h-1.5 rounded-full bg-background-50 animate-pulse-dot" />
                        正在直播
                      </span>
                    ) : (
                      <span className="rounded-md bg-secondary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
                        即将开赛
                      </span>
                    )}
                    <span className="text-xs text-background-100/80">
                      {formatDate(game.date)} {game.time} · {game.arena}
                    </span>
                  </div>

                  <h2 className="font-heading text-3xl md:text-5xl font-bold leading-tight text-background-50">
                    {away.fullName}
                    <span className="mx-2 text-accent-400">VS</span>
                    {home.fullName}
                  </h2>

                  {game.status !== "upcoming" && (
                    <div className="mt-6 flex items-end gap-4">
                      <div className="text-center">
                        <p className="text-xs text-background-100/70 mb-1">{away.abbreviation}</p>
                        <p className="font-heading text-4xl md:text-5xl font-bold text-background-50">{game.awayScore}</p>
                      </div>
                      <span className="pb-3 font-heading text-2xl text-background-100/50">-</span>
                      <div className="text-center">
                        <p className="text-xs text-background-100/70 mb-1">{home.abbreviation}</p>
                        <p className="font-heading text-4xl md:text-5xl font-bold text-background-50">{game.homeScore}</p>
                      </div>
                      {game.status === "live" && game.clock && (
                        <span className="pb-3 text-sm text-accent-300">{game.quarter} {game.clock}</span>
                      )}
                    </div>
                  )}

                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <Link
                      to={`/detail/${game.id}`}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-play-circle-line text-lg" />
                      {game.status === "live" ? "立即观看直播" : game.status === "final" ? "查看战报与回放" : "查看赛事详情"}
                    </Link>
                    <Link
                      to="/category/2025-2026"
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-background-100/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-calendar-line text-lg" />
                      查看完整赛程
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => go(index - 1)}
        className="absolute left-3 md:left-6 top-1/2 z-10 w-9 h-9 md:w-10 md:h-10 flex -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 cursor-pointer"
        aria-label="上一场"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        className="absolute right-3 md:right-6 top-1/2 z-10 w-9 h-9 md:w-10 md:h-10 flex -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 cursor-pointer"
        aria-label="下一场"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {games.map((game, i) => (
          <button
            key={game.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? "w-6 bg-accent-500" : "w-2.5 bg-background-50/50"}`}
            aria-label={`切换到第 ${i + 1} 场`}
          />
        ))}
      </div>
    </section>
  );
}